import React, { useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import {
  IoCloseOutline, IoDownloadOutline, IoReceiptOutline,
  IoStorefrontOutline, IoPersonOutline,
  IoCarOutline, IoCalendarOutline, IoCardOutline, IoCheckmarkCircle,
} from "react-icons/io5";
import Barcode from 'react-barcode';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDate, formatTimeInTimezone } from 'pages/utils/formdatedynamics';
import { getReadableOrderId } from 'pages/utils/HelperFun';

// ─────────────────────────────────────────────────────────────────────────────
//  BILLING LOGIC
//  • platformFee  = 5% of cost, clamped  min 1.50 AED – max 5.00 AED
//  • customerTotal = cost + addOns − discount          (what customer paid)
//  • shopTotal     = customerTotal − platformFee        (what shop receives)
// ─────────────────────────────────────────────────────────────────────────────
const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const computeBilling = (invoiceData, customer) => {
  const cost        = toNum(invoiceData?.cost);
  const addOnsTotal = toNum(invoiceData?.addOnsTotal ?? invoiceData?.orderDetails?.addOnsTotal);
  const discount    = toNum(invoiceData?.discount);

  const rawServiceFee = cost * 0.05;
  const platformFee   = parseFloat(Math.min(5.00, Math.max(1.50, rawServiceFee)).toFixed(2));
  const vatFee        = 0;

  const chargedAmount  = cost + addOnsTotal + platformFee - discount;
  const stripeFee      = parseFloat((chargedAmount * 0.029 + 0.30).toFixed(2));
  const commission     = parseFloat((cost * 0.08).toFixed(2));
  const washtaNet      = parseFloat((commission + platformFee - stripeFee).toFixed(2));

  const customerTotal  = cost + addOnsTotal + platformFee - discount - vatFee;
  const shopTotal      = customerTotal - platformFee;
  const displayTotal   = customer === true ? customerTotal : shopTotal;

  let promoLabel = null;
  const promo = invoiceData?.promoCode;
  if (promo?.promoCode && discount > 0) {
    promoLabel = promo.Discounttype === 'percentage'
      ? `${promo.promoCode} · ${promo.discount}% off`
      : `${promo.promoCode} · Fixed −${discount} AED`;
  }

  return {
    cost, addOnsTotal, discount,
    platformFee, vatFee, stripeFee, commission, washtaNet,
    customerTotal, shopTotal, displayTotal,
    promoLabel,
  };
};

// ─── sub-components ───────────────────────────────────────────────────────────

const DotRow = ({ label, value, deduct, accent, indent, muted }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 0',
    paddingLeft: indent ? 10 : 0,
    borderBottom: '0.5px solid #f5f5f5',
  }}>
    <span style={{
      fontSize: 12,
      color: accent ? '#185FA5' : muted ? '#bbb' : '#555',
      flex: 1,
      lineHeight: 1.4,
    }}>
      {label}
    </span>
    <span style={{
      fontSize: accent ? 14 : 12,
      fontWeight: accent ? 700 : deduct ? 500 : 400,
      color: deduct ? '#d97706' : accent ? '#185FA5' : '#1a1a1a',
      marginLeft: 12,
      whiteSpace: 'nowrap',
    }}>
      {value}
    </span>
  </div>
);

const SectionLabel = ({ icon: Icon, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 5, margin: '14px 0 7px' }}>
    {Icon && <Icon size={11} color="#c0c0c0" />}
    <span style={{
      fontSize: 9.5, fontWeight: 700, letterSpacing: '0.11em',
      textTransform: 'uppercase', color: '#c0c0c0',
    }}>
      {children}
    </span>
  </div>
);

const ThinDivider = ({ dashed }) => (
  <div style={{
    borderTop: dashed ? '1.5px dashed #ebebeb' : '0.5px solid #f0f0f0',
    margin: '10px 0',
  }} />
);

const InfoPair = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 6, fontSize: 12, lineHeight: 1.6, marginBottom: 1 }}>
    <span style={{ color: '#c0c0c0', minWidth: 105, flexShrink: 0 }}>{label}</span>
    <span style={{ color: '#1a1a1a', fontWeight: 500, flex: 1, wordBreak: 'break-word' }}>{value || '—'}</span>
  </div>
);

// ─── main ─────────────────────────────────────────────────────────────────────

const InvoicePopup = ({ show, onClose, invoiceData, customer }) => {
  const contentRef = useRef(null);
  if (!show || !invoiceData) return null;

  const {
    cost, addOnsTotal, discount,
    platformFee, vatFee, stripeFee, commission, washtaNet,
    customerTotal, shopTotal, displayTotal,
    promoLabel,
  } = computeBilling(invoiceData, customer);
  const orderId = getReadableOrderId
    ? getReadableOrderId(invoiceData?._id)
    : invoiceData?.orderId || invoiceData?._id || '—';

  const isPaid = invoiceData?.billingStatus?.toLowerCase() === 'paid';
  const statusStyle = isPaid
    ? { bg: '#F0FBE8', color: '#2E7D10', border: '#B6E08A' }
    : { bg: '#FFF7ED', color: '#B45309', border: '#FCD38A' };

  const handleDownload = async () => {
    const el = contentRef.current;
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2.5, backgroundColor: '#ffffff', useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = 190;
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth, pdfHeight);
    pdf.save(`invoice-${orderId}.pdf`);
  };

  return (
    <Dialog
      open={show}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        style: {
          borderRadius: 18,
          boxShadow: '0 24px 64px rgba(0,0,0,0.14)',
          overflow: 'hidden',
          background: '#fff',
        }
      }}
    >
      {/* ══ HEADER ══ */}
      <DialogTitle style={{ padding: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '13px 16px',
          background: '#fff',
          borderBottom: '0.5px solid #f0f0f0',
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: 'linear-gradient(135deg, #EEF5FF 0%, #ddeeff 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <IoReceiptOutline size={16} color="#185FA5" />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111', letterSpacing: '-0.01em' }}>
              E-Receipt
            </p>
            <p style={{ margin: 0, fontSize: 10, color: '#bbb', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
              {orderId}
            </p>
          </div>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 9px', borderRadius: 100, fontSize: 10.5, fontWeight: 600,
            background: statusStyle.bg, color: statusStyle.color,
            border: `0.5px solid ${statusStyle.border}`,
          }}>
            {isPaid && <IoCheckmarkCircle size={11} />}
            {invoiceData?.billingStatus || 'Pending'}
          </span>
          <IconButton size="small" onClick={onClose} style={{ marginLeft: 2, padding: 4 }}>
            <IoCloseOutline size={17} color="#999" />
          </IconButton>
        </div>
      </DialogTitle>

      {/* ══ SCROLL BODY ══ */}
      <DialogContent style={{ padding: '0 16px 8px', background: '#fff', overflowX: 'hidden' }}>
        <div ref={contentRef} style={{ background: '#fff', paddingBottom: 4 }}>

          {/* ── Barcode ── */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '16px 0 12px',
            borderBottom: '1.5px dashed #ebebeb',
          }}>
            <Barcode
              value={String(orderId)}
              displayValue={false}
              width={1.4}
              height={38}
              margin={0}
              background="transparent"
            />
            <span style={{
              fontSize: 9, color: '#d0d0d0', marginTop: 6,
              fontFamily: 'monospace', letterSpacing: '0.15em',
            }}>
              {orderId}
            </span>
          </div>

          {/* ── Shop ── */}
          <SectionLabel icon={IoStorefrontOutline}>Shop</SectionLabel>
          <InfoPair label="Name"     value={invoiceData?.shopId?.shopName} />
          <InfoPair label="Location" value={invoiceData?.shopId?.location?.display_name || invoiceData?.shopId?.location?.text} />

          <ThinDivider />

          {/* ── Customer ── */}
          <SectionLabel icon={IoPersonOutline}>Customer</SectionLabel>
          <InfoPair label="Email"    value={invoiceData?.customerId?.email} />
          {invoiceData?.customerId?.phone && (
            <InfoPair label="Phone"  value={invoiceData.customerId.phone} />
          )}
          <InfoPair label="Location" value={invoiceData?.location?.display_name || invoiceData?.location?.text || 'Not provided'} />

          <ThinDivider />

          {/* ── Vehicle ── */}
          <SectionLabel icon={IoCarOutline}>Vehicle</SectionLabel>
          <InfoPair
            label="Car"
            value={[invoiceData?.vehicleId?.vehicleName, invoiceData?.vehicleId?.vehicleType].filter(Boolean).join(' · ')}
          />
          <InfoPair label="Plate no." value={invoiceData?.vehicleId?.vehiclePlateNumber} />

          <ThinDivider />

          {/* ── Booking ── */}
          <SectionLabel icon={IoCalendarOutline}>Booking</SectionLabel>
          <InfoPair
            label="Date & time"
            value={
              invoiceData?.date
                ? `${formatDate(invoiceData.date)} · ${formatTimeInTimezone(invoiceData.date)}`
                : invoiceData?.createdAt
                ? `${formatDate(invoiceData.createdAt)} · ${formatTimeInTimezone(invoiceData.createdAt)}`
                : '—'
            }
          />
          <InfoPair label="Service" value={invoiceData?.serviceVariant?.variantName || invoiceData?.carServiceName} />
          {invoiceData?.serviceVariant?.variantTypeLabel && (
            <InfoPair label="Type"   value={invoiceData.serviceVariant.variantTypeLabel} />
          )}
          {invoiceData?.carParkedAt && (
            <InfoPair label="Parked at" value={invoiceData.carParkedAt} />
          )}
          {invoiceData?.paidAt && (
            <InfoPair label="Paid at" value={`${formatDate(invoiceData.paidAt)} · ${formatTimeInTimezone(invoiceData.paidAt)}`} />
          )}

          {/* ══ BILLING BREAKDOWN ══ */}
          <ThinDivider dashed />
          <SectionLabel icon={IoCardOutline}>Billing</SectionLabel>

          {/* Base price */}
          <DotRow label="Shop base price" value={`${cost.toFixed(2)} AED`} />
          <DotRow label="Service Fee" value={`${platformFee.toFixed(2)} AED`} />

          {/* Add-ons */}
          {addOnsTotal > 0 && (
            <DotRow label="Add-ons" value={`+ ${addOnsTotal.toFixed(2)} AED`} indent />
          )}

          {/* Promo / discount */}
          {discount > 0 && (
            <DotRow
              label={promoLabel || 'Promo discount'}
              value={`− ${discount.toFixed(2)} AED`}
              deduct indent
            />
          )}

          {/* Washta platform fee, Stripe fee, Commission — admin/shop only */}
          {customer !== true && (
            <>
              <DotRow
                label="Washta platform fee (5% · min 1.50 – max 5.00 AED)"
                value={`− ${platformFee.toFixed(2)} AED`}
                deduct indent
              />
              <DotRow
                label="Stripe processing fee (2.9% + AED 0.30)"
                value={`− ${stripeFee.toFixed(2)} AED`}
                deduct indent
              />
              <DotRow
                label="Washta commission (8% of base price)"
                value={`+ ${commission.toFixed(2)} AED`}
                indent
              />
            </>
          )}

          {/* VAT if applicable */}
          {vatFee > 0 && (
            <DotRow label="VAT" value={`+ ${vatFee.toFixed(2)} AED`} indent />
          )}

          {/* — Washta net (admin only) — */}
          {customer !== true && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#f0fbe8', border: '0.5px solid #b6e08a',
              borderRadius: 8, padding: '7px 12px', marginTop: 6,
            }}>
              <span style={{ fontSize: 11, color: '#1a6641' }}>Washta net (commission + platform − stripe)</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1a6641' }}>
                {washtaNet.toFixed(2)} <span style={{ fontSize: 10, fontWeight: 400 }}>AED</span>
              </span>
            </div>
          )}

          {/* ── Subtotal stripe (customer paid) — always visible to admin ── */}
          {customer !== true && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#fafaf9',
              border: '0.5px solid #ececea',
              borderRadius: 8,
              padding: '7px 12px',
              marginTop: 8,
            }}>
              <span style={{ fontSize: 11, color: '#aaa' }}>Customer paid</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>
                {customerTotal.toFixed(2)} <span style={{ fontSize: 10, fontWeight: 400, color: '#bbb' }}>AED</span>
              </span>
            </div>
          )}

          {/* ── Total box ── */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'linear-gradient(135deg, #185FA5 0%, #1a7acc 100%)',
            borderRadius: 10,
            padding: '11px 14px',
            marginTop: 8,
          }}>
            <div>
              <p style={{ margin: 0, fontSize: 10, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {customer === true ? 'Amount Paid' : 'Payment to Shop'}
              </p>
              <p style={{ margin: 0, fontSize: 9, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>
                {customer === true
                  ? 'Includes all charges'
                  : `After Washta fee (− ${platformFee.toFixed(2)} AED)`}
              </p>
            </div>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
              {displayTotal.toFixed(2)}{' '}
              <span style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>AED</span>
            </span>
          </div>

          {/* Payment ID */}
          {invoiceData?.paymentId && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 5, marginTop: 10,
            }}>
              <IoCardOutline size={10} color="#ddd" />
              <span style={{ fontSize: 9.5, color: '#ddd', fontFamily: 'monospace', letterSpacing: '0.08em' }}>
                {invoiceData.paymentId}
              </span>
            </div>
          )}

        </div>
      </DialogContent>

      {/* ══ FOOTER ══ */}
      <DialogActions style={{ padding: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderTop: '0.5px solid #f0f0f0',
          width: '100%',
          background: '#fafaf9',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 10, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {customer === true ? 'Total paid' : 'Net to shop'}
            </p>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111', lineHeight: 1.2 }}>
              {displayTotal.toFixed(2)}{' '}
              <span style={{ fontSize: 11, fontWeight: 400, color: '#bbb' }}>AED</span>
            </p>
          </div>

          <button
            onClick={handleDownload}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', borderRadius: 9,
              border: 'none',
              background: 'linear-gradient(135deg, #185FA5 0%, #1a7acc 100%)',
              color: '#fff', fontSize: 12, fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 3px 10px rgba(24,95,165,0.30)',
              letterSpacing: '0.01em',
            }}
          >
            <IoDownloadOutline size={14} />
            Download Receipt
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePopup;